<template>
    <div
        class="product-img-thumbs d-flex"
        :class="{
            'flex-column': isVertical,
            'type-thumbs': isThumbs,
            'type-dots': !isThumbs,
            'opacity-50': disabled
        }"
    >
        <template
            v-for="img in productImages"
            :key="`img-thumb-${img.id || 'default'}`"
        >
            <a
                :href="`#product-main-image-${img.uid}`"
                class="
                    d-flex
                    no-outline
                    text-decoration-none
                "
                :class="[{
                    active: img.uid === selectedImageUid,
                    'pa-1': isThumbs
                }, itemClass]"
                @click.prevent="emit('on-change', `${selectorPrefix}-${img.uid}`)"
            >
                <div
                    v-if="isThumbs"
                    class="position-relative overflow-hidden flex-grow-1"
                >
                    <ProductsLayeredImage
                        :image-object="img"
                        :image-stack="imageStack"
                        :top-image-props="{
                            class: 'object-fit-contain aspect-ratio-1 w-100',
                            label: img.alt,
                            type: 'x-small',
                            disableDropShadow: true
                        }"
                        :low-images-props="{
                            class: 'object-fit-contain aspect-ratio-1 w-100',
                            type: 'x-small',
                            disableDropShadow: true
                        }"
                    ></ProductsLayeredImage>
                </div>
                <div
                    v-else
                ></div>
            </a>
        </template>

        <!-- YouTube Video -->
        <template
            v-if="youtubeId"
        >
            <a
                href="#product-main-image-0"
                title="Watch the Video"
                :class="{
                    active: selectedImageUid === '0',
                    'pa-1': isThumbs
                }"
                class="
                    d-flex
                    no-outline
                    text-decoration-none
                    aspect-ratio-1
                    align-center
                    justify-center
                "
                @click.prevent="!disabled && emit('on-change', '#product-main-image-0')"
            >
                <v-icon
                    v-if="isThumbs"
                    size="46px"
                    :icon="mdiPlayCircle"
                ></v-icon>
                <v-icon
                    v-else
                    tag="div"
                    size="12px"
                    :icon="mdiPlay"
                    class="rounded-circle"
                ></v-icon>
            </a>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import type {
        FullImage,
        FullImageStack
    } from '~/types';
    import ProductsLayeredImage from '~/components/products/ProductsLayeredImage/ProductsLayeredImage.vue';
    import { mdiPlayCircle, mdiPlay } from '@mdi/js';

    const props = withDefaults(
        defineProps<{
            productImages: Nullable<FullImage[]>;
            selectedImageUid: Nullable<string>;
            youtubeId?: Nilish<string>;
            itemClass?: Nilish<string>;
            type?: 'thumbs' | 'dots';
            orientation?: 'vertical' | 'horizontal';
            selectorPrefix?: string;
            imageStack?: Nilish<FullImageStack>;
            disabled?: boolean;
        }>(),
        {
            type: 'thumbs',
            orientation: 'vertical',
            selectorPrefix: '#product-main-image',
            disabled: false
        }
    );

    const emit = defineEmits<{
        'on-change': [value: string];
    }>();

    const isThumbs = computed(() => props.type === 'thumbs');
    const isVertical = computed(() => props.orientation === 'vertical');
</script>

<style lang="scss">
$thumbs-border-thickness: 3px;

.product-img-thumbs {
    transition: all 0.1s ease;

    &.type-thumbs {
        gap: 0.6rem;

        a {
            width: 78px;
            height: 78px;
            border-radius: 10px;
            border: $thumbs-border-thickness solid rgba(var(--v-theme-on-surface), 0.04);

            &.active {
                border: $thumbs-border-thickness solid rgb(var(--v-theme-primary));
            }
        }
    }

    &.type-dots {
        gap: 0.8rem;

        a {
            position: relative;
            border-radius: 50%;

            div {
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background-color: rgba(var(--v-theme-on-surface), 0.4);

                svg {
                    filter: invert(1);
                }
            }

            &.active {
                div {
                    background-color: rgba(var(--v-theme-on-surface), 0.8);
                }
            }
        }
    }
}
</style>
